
import { Component, ChangeDetectionStrategy, inject, signal, OnInit, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DataService } from '../../services/data.service';
import { PosDevice, Store } from '../../models';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { PosDeviceFormModalComponent } from '../pos-device-form-modal/pos-device-form-modal.component';
import { NotificationService } from '../../services/notification.service';

@Component({
  selector: 'app-pos-device-management',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe, PosDeviceFormModalComponent],
  templateUrl: './pos-device-management.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PosDeviceManagementComponent implements OnInit {
  private dataService = inject(DataService);
  private notificationService = inject(NotificationService);

  allDevices = signal<PosDevice[]>([]);
  stores = signal<Store[]>([]);
  isLoading = signal(true);
  error = signal<string | null>(null);

  selectedStoreId = signal<string>('all');
  
  filteredDevices = computed(() => {
    const storeId = this.selectedStoreId();
    if (storeId === 'all') {
      return this.allDevices();
    }
    return this.allDevices().filter(device => device.storeId === storeId);
  });

  // Modal State
  isModalOpen = signal(false);
  editingDevice = signal<PosDevice | null>(null);

  ngOnInit(): void {
    this.loadData();
  }

  async loadData(): Promise<void> {
    this.isLoading.set(true);
    this.error.set(null);
    try {
      const [devices, stores] = await Promise.all([
        this.dataService.getPosDevices(),
        this.dataService.getStores(),
      ]);
      this.allDevices.set(devices.sort((a, b) => a.name.localeCompare(b.name)));
      this.stores.set(stores.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : 'Failed to load POS device data');
    } finally {
      this.isLoading.set(false);
    }
  }
  
  openAddModal(): void {
    this.editingDevice.set(null);
    this.isModalOpen.set(true);
  }

  openEditModal(device: PosDevice): void {
    this.editingDevice.set(device);
    this.isModalOpen.set(true);
  }

  handleCloseModal(): void {
    this.isModalOpen.set(false);
    this.editingDevice.set(null);
  }

  async handleSaveDevice(deviceData: Omit<PosDevice, 'id' | 'createdAt'> | PosDevice): Promise<void> {
    try {
      if ('id' in deviceData && deviceData.id) {
        await this.dataService.updatePosDevice(deviceData as PosDevice);
      } else {
        await this.dataService.addPosDevice(deviceData as Omit<PosDevice, 'id' | 'createdAt'>);
      }
      this.handleCloseModal();
      this.loadData(); // Refresh list
    } catch(e) {
      this.notificationService.showError(`Error saving device: ${e instanceof Error ? e.message : 'An unknown error occurred'}`);
    }
  }

  async deleteDevice(device: PosDevice): Promise<void> {
    if (confirm(`Are you sure you want to delete the device "${device.name}"?`)) {
      try {
        await this.dataService.deletePosDevice(device.id);
        this.loadData();
      } catch (e) {
        this.notificationService.showError(`Error deleting device: ${e instanceof Error ? e.message : 'An unknown error occurred'}`);
      }
    }
  }
}
